import { RefreshCw, Save, Search } from 'lucide-react'
import { useId } from 'react'

import { Button, StatusMessage, TextField } from '@krp/ui-kit'

import type { PricingOverviewActions, PricingOverviewVm } from '../model/types'
import { FreshnessBadge } from './FreshnessBadge'
import { QueryState } from './QueryState'
import './onec-ui.css'

/**
 * Окно «Цены и остатки» базы 1С: сверка розничных цен и остатки/цены по
 * набору товаров. Экран получает vm и actions и больше ничего (R02).
 */
export function PricingOverviewScreen({ vm, actions }: { vm: PricingOverviewVm; actions: PricingOverviewActions }) {
  const keysId = useId()
  return (
    <section className="onec-screen" aria-labelledby="onec-pricing-title">
      <header className="onec-section__head">
        <h2 id="onec-pricing-title">Цены и остатки</h2>
        <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload}>
          Обновить
        </Button>
      </header>

      <section className="onec-section" aria-labelledby="onec-pricing-discrepancies">
        <div className="onec-section__head">
          <h3 id="onec-pricing-discrepancies">Расхождения розничных цен</h3>
          <FreshnessBadge freshness={vm.discrepancies.freshness} />
        </div>
        <p className="onec-muted">
          Товары, у которых цены двух видов расходятся. Пара видов цен — ключи 1С (GUID); сохранённая пара личная.
        </p>
        <QueryState query={vm.filter.query} emptyText="Пара не задана" />
        <div className="onec-row">
          <TextField label="Первый вид цен" value={vm.filter.draft.first} onChange={(value) => actions.pairChanged('first', value)} />
          <TextField label="Второй вид цен" value={vm.filter.draft.second} onChange={(value) => actions.pairChanged('second', value)} />
        </div>
        {vm.filter.problem !== null && <p className="onec-problem">{vm.filter.problem}</p>}
        {vm.filter.saved === null ? (
          <p className="onec-muted">Личная пара ещё не сохранена</p>
        ) : (
          <p className="onec-muted">Сохранена {vm.filter.saved.updatedAtText}</p>
        )}
        <div className="onec-row">
          <Button
            variant="primary"
            icon={<Save size={16} aria-hidden="true" />}
            busy={vm.save.busy}
            disabled={!vm.filter.canSave || vm.configurationVersion === null}
            onClick={actions.pairSaved}
          >
            Сохранить пару
          </Button>
          <Button disabled={!vm.filter.dirty || vm.filter.problem !== null} onClick={actions.compare}>
            Сверить без сохранения
          </Button>
          {vm.filter.dirty && <Button variant="ghost" onClick={actions.pairDiscarded}>Сбросить</Button>}
        </div>
        {vm.comparing !== null && <p className="onec-muted">Показана сверка для несохранённой пары</p>}
        {vm.save.error !== null && (
          <StatusMessage
            kind="error"
            title="Пара не сохранена"
            text={vm.save.error.message}
            meta={`Код: ${vm.save.error.code}${vm.save.error.correlationId === null ? '' : ' · запрос ' + vm.save.error.correlationId}`}
          />
        )}
        {vm.save.saved && vm.save.error === null && <p className="onec-muted">Пара сохранена</p>}

        <QueryState query={vm.discrepancies.query} emptyText="Расхождений нет" onRetry={actions.reload} />
        {vm.discrepancies.query.status === 'ready' && (
          <div className="onec-scroll">
            <table className="onec-table">
              <caption className="onec-muted">Найдено расхождений: {vm.discrepancies.rows.length}</caption>
              <thead>
                <tr>
                  <th>Товар</th>
                  <th>Артикул</th>
                  <th>Цены по видам</th>
                </tr>
              </thead>
              <tbody>
                {vm.discrepancies.rows.map((row) => (
                  <tr key={row.productId}>
                    <td>{row.name}</td>
                    <td>{row.sku}</td>
                    <td>
                      {row.prices.map((price) => (
                        <div key={price.priceType}>
                          <span className="onec-code">{price.priceType}</span>: {price.priceText}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="onec-section" aria-labelledby="onec-pricing-lookup">
        <h3 id="onec-pricing-lookup">Остатки и цены по товарам</h3>
        <div className="krp-field">
          <label className="krp-field__label" htmlFor={keysId}>
            Ключи товаров 1С (по одному на строку)
          </label>
          <textarea
            id={keysId}
            className="onec-textarea"
            value={vm.lookup.keysText}
            onChange={(event) => actions.keysTextChanged(event.target.value)}
            spellCheck={false}
          />
        </div>
        {vm.lookup.problem !== null && <p className="onec-problem">{vm.lookup.problem}</p>}
        {vm.configurationVersion === null && (
          <p className="onec-muted">Запрос станет доступен, когда база ответит на сверку цен.</p>
        )}
        <div className="onec-row">
          <Button
            variant="primary"
            icon={<Search size={16} aria-hidden="true" />}
            disabled={!vm.lookup.canLookup}
            onClick={actions.lookup}
          >
            Показать остатки и цены ({vm.lookup.keys.length})
          </Button>
        </div>

        <div className="onec-section__head">
          <h4>Остатки</h4>
          <FreshnessBadge freshness={vm.lookup.stock.freshness} />
        </div>
        <QueryState query={vm.lookup.stock.query} emptyText="Остатков по этим товарам нет" onRetry={actions.lookup} />
        {vm.lookup.stock.query.status === 'ready' && (
          <div className="onec-scroll">
            <table className="onec-table">
              <thead>
                <tr>
                  <th>Товар</th>
                  <th>Склад</th>
                  <th data-numeric="true">Остаток</th>
                </tr>
              </thead>
              <tbody>
                {vm.lookup.stock.rows.map((row) => (
                  <tr key={`${row.productId}:${row.warehouse}`}>
                    <td className="onec-code">{row.productId}</td>
                    <td className="onec-code">{row.warehouse}</td>
                    <td data-numeric="true">{row.quantityText}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="onec-section__head">
          <h4>Цены</h4>
          <FreshnessBadge freshness={vm.lookup.prices.freshness} />
        </div>
        <QueryState query={vm.lookup.prices.query} emptyText="Цен по этим товарам нет" onRetry={actions.lookup} />
        {vm.lookup.prices.query.status === 'ready' && (
          <div className="onec-scroll">
            <table className="onec-table">
              <thead>
                <tr>
                  <th>Товар</th>
                  <th>Вид цен</th>
                  <th data-numeric="true">Цена</th>
                </tr>
              </thead>
              <tbody>
                {vm.lookup.prices.rows.map((row) => (
                  <tr key={`${row.productId}:${row.priceType}`}>
                    <td className="onec-code">{row.productId}</td>
                    <td className="onec-code">{row.priceType}</td>
                    <td data-numeric="true">{row.priceText}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </section>
  )
}
